/**
 * ChallengeWiring — Connects the ChallengeOverlayBridge, EduAdventureMotorAdapter,
 * and GameStateCoordinator into a working challenge flow.
 *
 * Flow:
 * 1. Player enters a cave trigger in the overworld
 * 2. GameStateCoordinator.startChallenge(caveId) is called
 * 3. Coordinator pauses Phaser, shows overlay, displays "Pronto?" prompt
 * 4. If confirmed: loads challenge data, initializes motor via ChallengeOverlayBridge
 * 5. Motor runs the educational challenge (tap_choice, drag_match, etc.)
 * 6. On completion: motor signals → bridge calls onChallengeComplete → coordinator.completeChallenge()
 * 7. On exit: motor signals → bridge calls onChallengeExit → coordinator.exitChallenge()
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 4.7, 10.5
 */

import { ChallengeOverlayBridge, type ChallengeOverlayBridgeConfig } from './ChallengeOverlayBridge';
import { GameStateCoordinator, type ChallengeDataProvider } from './GameStateCoordinator';
import { IframeMotorFactory } from './EduAdventureMotorAdapter';
import type { CaveDefinition } from '../types/world-config.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChallengeWiringConfig {
  /** The DOM container where the overlay will be appended (typically #phaser-container) */
  container: HTMLElement;
  /** The GameStateCoordinator instance (from GameSceneWiring) */
  gameStateCoordinator: GameStateCoordinator;
  /** All cave definitions from the current map's world-config */
  caves: CaveDefinition[];
}

export interface ChallengeWiringResult {
  /** The overlay bridge instance (for direct access if needed) */
  overlayBridge: ChallengeOverlayBridge;
  /** Cleanup function to destroy the wiring */
  destroy: () => void;
}

// ─── Data Provider ────────────────────────────────────────────────────────────

/**
 * Simple ChallengeDataProvider that looks up cave definitions from a map.
 */
class MapChallengeDataProvider implements ChallengeDataProvider {
  private _caves: Map<string, CaveDefinition>;

  constructor(caves: CaveDefinition[]) {
    this._caves = new Map();
    for (const cave of caves) {
      this._caves.set(cave.id, cave);
    }
  }

  getCaveDefinition(caveId: string): CaveDefinition | undefined {
    return this._caves.get(caveId);
  }
}

// ─── Main Wiring Function ─────────────────────────────────────────────────────

/**
 * Wires the challenge overlay system into the game.
 *
 * Call this after wireGameSystems() in the GameScene, passing the
 * gameStateCoordinator from WiredSystems and the current map's caves.
 *
 * @param config - Wiring configuration
 * @returns ChallengeWiringResult with the bridge and a destroy function
 */
export function wireChallengeOverlay(config: ChallengeWiringConfig): ChallengeWiringResult {
  const { container, gameStateCoordinator, caves } = config;

  // ─── 1. Create the data provider ───────────────────────────────────────────

  const dataProvider = new MapChallengeDataProvider(caves);
  gameStateCoordinator.setDataProvider(dataProvider);

  // ─── 2. Create the ChallengeOverlayBridge with IframeMotorFactory ──────────

  const bridgeConfig: ChallengeOverlayBridgeConfig = {
    container,
    zIndex: 1000,
    motorFactory: new IframeMotorFactory(),
    onChallengeComplete: (caveId: string) => {
      gameStateCoordinator.completeChallenge(caveId);
    },
    onChallengeExit: () => {
      gameStateCoordinator.exitChallenge();
    },
  };

  const overlayBridge = new ChallengeOverlayBridge(bridgeConfig);

  // ─── 3. Wire the bridge as the overlay callbacks for the coordinator ───────

  gameStateCoordinator.setOverlayCallbacks({
    show: () => overlayBridge.show(),
    hide: () => overlayBridge.hide(),
    showPrompt: (message: string) => overlayBridge.showPrompt(message),
    initializeMotor: (gameJson: object) => overlayBridge.initializeMotor(gameJson),
    stopMotor: () => overlayBridge.stopMotor(),
  });

  // ─── 4. Wire the active cave ID tracking ──────────────────────────────────

  gameStateCoordinator.on('stateChange', (newState, _previousState) => {
    if (newState === 'CHALLENGE_TRANSITION' || newState === 'CHALLENGE_ACTIVE') {
      overlayBridge.setActiveCaveId(gameStateCoordinator.activeCaveId);
    } else {
      overlayBridge.setActiveCaveId(null);
    }
  });

  // ─── 5. Build destroy function ─────────────────────────────────────────────

  const destroy = (): void => {
    overlayBridge.destroy();
  };

  return {
    overlayBridge,
    destroy,
  };
}
